<template>
  <div class="app">
    <cl-sidebar v-model="isClosed">
      <template #company-logo="{ isClosed }">
        <t-logo
          v-show="!isClosed"
          @click.prevent="redirectBasedOnRole"
          :logo-src="logoSrc"
        />
      </template>
      <template #dropdown-select="{ isClosed }">
        <t-nav-dropdown
          data-test-id="navbar-select-msp"
          :loading="loading"
          :is-closed="isClosed"
          :options="mspAccountList"
          :initial-selected="initialMsp"
          data-clarity-mask="true"
          @reset-list="setDropDownList"
          @search="itemBeingSearched"
          @selected="mspSelected"
        />
      </template>
      <template #side-nav="{ isClosed: isSidebarCollapsed }">
        <t-side-nav
          :nav-menu-set-up="systemNavMenuSetUp"
          :is-sidebar-collapsed="isSidebarCollapsed"
          :tier="tier"
        ></t-side-nav>
      </template>
      <template #info-section="{ isClosed: isSidebarCollapsed }">
        <img
          v-if="!isSidebarCollapsed"
          alt="CyberSentriq logo"
          :src="cyberSentriqLogoSrc"
        />
      </template>
    </cl-sidebar>
    <div class="app-content">
      <t-upper-nav />
      <main
        class="tw-flex-1 tw-overflow-y-auto tw-bg-light tw-p-4 max-lg:tw-ml-14"
      >
        <read-only-role-banner
          :visible="isReadOnlyRoleVisible"
          level="system"
        />
        <div class="tw-w-full">
          <t-page-header
            :product-links="productLinks"
            :user-links="userLinks"
            :data-test-id="'header-user-menu'"
          />
          <router-view />
        </div>
      </main>
      <cl-footer />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import TPageHeader from "@/components/TPageHeader";
import TNavDropdown from "@/components/TNavDropdown";
import TSideNav from "@/components/TSideNav";
import { usePrimaryNavItems, useRedirectBasedOnRole } from "@/composables";
import { NAV_ITEMS_MODE, TIER } from "@/constants";
import TLogo from "@/components/TLogo/TLogo.vue";
import TUpperNav from "@/components/TUpperNav/TUpperNav.vue";
import ReadOnlyRoleBanner from "@/views/Common/ReadOnlyRoleBanner.vue";
import type { Organisation } from "@/types/organisatio";
import type {
  ProductLink,
  UserLink,
} from "@/components/TPageHeader/TPageHeader.types";
import { $userPrefs } from "@/plugins/userPrefs";
import { getAccountShortName } from "@/utils/getAccountShortName";
import type { DataItem } from "@/components/TSideNav/types";
import { storeToRefs } from "pinia";
import { useSidebarStore } from "@/store/sidebar";

type DropdownItem = {
  id: string | number;
  name: string;
  short?: string;
  testID?: string;
  AllMsps?: boolean;
  allCustomers?: boolean;
  placeholder?: string;
};

type MspAccount = Organisation &
  DropdownItem & {
    short: string;
    testID: string;
    AllMsps?: boolean;
    allCustomers?: boolean;
    placeholder?: string;
  };

type SearchParams = {
  filter: string;
  results: number;
  page?: number;
};

type AuthUser = {
  uuid: string;
  accountname: string;
  organisation: string;
  email: string;
  role: string;
  level: string;
};

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const { navItems } = usePrimaryNavItems();
const systemNavItems = navItems.get(NAV_ITEMS_MODE.SYSTEM);
const redirectBasedOnRole = useRedirectBasedOnRole();
const logoSrc = new URL("@/assets/branding/Titan_Logo.svg", import.meta.url)
  .href;
const cyberSentriqLogoSrc = new URL(
  "@/assets/branding/powered-by-cybersentriq.svg",
  import.meta.url,
).href;

// Reactive data
const loading = ref<boolean>(false);
const mspAccountList = ref<DropdownItem[]>([]);
const initialMsp = ref<string | number | undefined>(undefined);
const tier = ref<string>(TIER.system);
const params = ref<SearchParams>({
  filter: "",
  results: 20,
});

const productLinks = ref<ProductLink[]>([]);
const userLinks = ref<UserLink[]>([]);
const sidebarStore = useSidebarStore();
const { isClosed } = storeToRefs(sidebarStore);

const dropDownList = computed<Organisation[]>(
  () => store.getters["dropDown/dropDownList"],
);
const authUser = computed<AuthUser>(
  () => store.getters["authentication/authUser"],
);

const isReadOnlyRoleVisible = computed<boolean>(() => {
  return authUser.value?.role === "admin-ro";
});

const separatedByGroup = (mspArray: DataItem[]): Record<string, DataItem[]> => {
  const mspByGroup: Record<string, DataItem[]> = {};
  const alpha: DataItem[] = [];

  mspArray.forEach((item) => {
    if ("groupBy" in item) {
      if (mspByGroup[item.groupBy]) {
        mspByGroup[item.groupBy].push(item);
      } else {
        mspByGroup[item.groupBy] = [item];
      }
    } else {
      alpha.push(item);
    }
  });

  return mspByGroup;
};

const systemNavMenuSetUp = computed((): Record<string, DataItem[]> => {
  return separatedByGroup(systemNavItems);
});

const setSupportLinks = (): void => {
  const id = authUser.value?.uuid;
  userLinks.value = [
    {
      title: t("Security"),
      link: `/profile/security?id=${id}`,
      target: "_blank",
      icon: "cl-icon-tabler-settings",
    },
  ];
};

const setDropDownList = async (): Promise<void> => {
  try {
    await store.dispatch("dropDown/getDropDownList", {
      parent_uuid: null,
      params: params.value,
    });
  } finally {
    mspAccountList.value = cleanMspAccounts(dropDownList.value);
    loading.value = false;
  }
};

const itemBeingSearched = async (item: string): Promise<void> => {
  try {
    const searchParams: SearchParams = {
      filter: item,
      results: 20,
      page: 1,
    };
    await store.dispatch("dropDown/getDropDownList", {
      parent_uuid: null,
      params: searchParams,
    });
  } finally {
    loading.value = false;
  }
};

const mspSelected = async (msp: DropdownItem): Promise<void> => {
  store.dispatch("authentication/setMspUser", msp);
  if (msp.AllMsps) {
    router.push("/system/dashboard").catch(() => {});
  } else if (
    route.path !== "/msp/customers" &&
    route.path !== "/msp/overview"
  ) {
    router.push({ name: "mspOverview" }).catch(() => {});
  }
};

const cleanMspAccounts = (accounts: Organisation[]): MspAccount[] => {
  const allAccounts = accounts
    .map((acct, index) => ({
      ...acct,
      name: acct.name || "",
      testID: `navbar-select-customer-${index}`,
      AllMsps: false,
      allCustomers: false,
      short: getAccountShortName(acct.name),
    }))
    .filter((res) => res.organisation_type === "MSP")
    .sort((a, b) =>
      a.name.toLowerCase() > b.name.toLowerCase()
        ? 1
        : b.name.toLowerCase() > a.name.toLowerCase()
          ? -1
          : 0,
    );

  return [
    {
      uuid: "",
      name: t("menu-item-all-msps"),
      short: "AM",
      id: 0,
      AllMsps: true,
      allCustomers: false,
      placeholder: "Search MSPs",
      country: "",
      created_at: "",
      organisation_type: "MSP",
      services: [],
      testID: "navbar-select-all-msps",
    },
    ...allAccounts,
  ] as MspAccount[];
};

watch(
  dropDownList,
  () => {
    mspAccountList.value = cleanMspAccounts(dropDownList.value);
  },
  { deep: true, immediate: true },
);

onBeforeMount(() => {
  const userToken = $userPrefs.getCurrentToken();
  if (!userToken) router.push("/");

  store.dispatch("pageMeta/setParentPath", "");
  store.dispatch("authentication/setMspUser", {
    name: t("menu-item-all-msps"),
    short: "AC",
    id: 0,
    AllMsps: true,
    license: "ABC-123",
    placeholder: "Search MSPs",
  });
  store.dispatch("authentication/setCustomer", null);
});

onMounted(async () => {
  setSupportLinks();
});
</script>
